'use client';

import { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import HeroScrollWrapper from '@/components/HeroScrollWrapper';
import UpcomingSessions from '@/components/UpcomingSessions';
import Alert from '@/components/Alert';
import Gallery from '@/components/Gallery';
import Pricing from '@/components/Pricing';
import About from '@/components/About';
import InstagramFeed from '@/components/InstagramFeed';
import Footer from '@/components/Footer';
import LandingStickyBar from '@/components/LandingStickyBar';
import LoadingScreen from '@/components/LoadingScreen';
import { PageLoadingProvider, usePageLoading } from '@/contexts/PageLoadingContext';
import { trackEvent, getUtmParams } from '@/lib/analytics/firebaseAnalytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import styles from './page.module.css';

const HOME_TITLE = 'Lior Murphy | Live Zoom Backbend and Splits Classes';

function HomeContent() {
  const { isLoading } = usePageLoading();
  const [mounted, setMounted] = useState(false);

  // Ensure hydration is complete before running effects
  useEffect(() => {
    setMounted(true);
  }, []);

  // Track landing page view with UTM parameters
  useEffect(() => {
    if (!mounted) return;

    document.title = HOME_TITLE;

    const utmParams = getUtmParams();
    const referrer = document.referrer;

    // Determine traffic source
    let trafficSource = 'direct';
    if (utmParams.utm_source) {
      trafficSource = utmParams.utm_source;
    } else if (referrer) {
      if (referrer.includes('instagram.com')) trafficSource = 'instagram';
      else if (referrer.includes('google.com')) trafficSource = 'google';
      else if (referrer.includes('facebook.com')) trafficSource = 'facebook';
      else if (referrer.includes('t.co') || referrer.includes('twitter.com')) trafficSource = 'twitter';
      else trafficSource = 'referral';
    }

    trackEvent(AnalyticsEvents.PAGE_VIEW, {
      page_title: HOME_TITLE,
      page_path: '/',
      traffic_source: trafficSource,
      referrer: referrer,
      ...utmParams,
    });
  }, [mounted]);

  // Re-resolve the URL hash after the page is fully mounted. The browser
  // performs an initial scroll to the hash before async images settle,
  // which leaves the section clipped under the header. We re-scroll the
  // page to the target a few times across the first second so that any
  // layout shifts caused by lazy-loaded media end with the user landing
  // on the section header (offset by the fixed header).
  useEffect(() => {
    if (!mounted || isLoading) return;
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    const id = decodeURIComponent(hash.slice(1));
    const target = document.getElementById(id);
    if (!target) return;

    const headerOffset = () =>
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-base-offset'
        )
      ) || 120;

    const scrollToTarget = (smooth: boolean) => {
      const top =
        target.getBoundingClientRect().top +
        window.scrollY -
        headerOffset() -
        0;
      window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
    };

    // Snap immediately, then re-correct over the next ~1s as images load.
    scrollToTarget(false);
    const t1 = window.setTimeout(() => scrollToTarget(true), 200);
    const t2 = window.setTimeout(() => scrollToTarget(true), 600);
    const t3 = window.setTimeout(() => scrollToTarget(true), 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [mounted, isLoading]);

  return (
    <>
      <LoadingScreen isLoading={isLoading} />

      <div
        style={{
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.2s ease-in',
        }}
      >
        <Header />
        <main className={styles.landing}>
          <HeroScrollWrapper>
            <Hero />
          </HeroScrollWrapper>
          <UpcomingSessions />
          <Alert />
          <Gallery />
          <Pricing />
          <About />
          <InstagramFeed />
          <Footer />
          <LandingStickyBar />
        </main>
      </div>
    </>
  );
}

export default function HomePage() {
  return (
    <PageLoadingProvider>
      <HomeContent />
    </PageLoadingProvider>
  );
}
